import React, { useEffect, useState, useRef } from 'react';
import { Box, Grid, Container, Snackbar, Alert, CircularProgress, Card, CardContent, Typography, Tabs, Tab } from '@mui/material';
import { Assessment as AssessmentIcon } from '@mui/icons-material';
import {
  Header,
  WhatSystemDoesCard,
  LatestRecommendationCard,
  ActionBar,
  AgentOrchestrationNetwork,
  ActionFeed,
  ResultsPanel,
  ChatSidebar,
  CheckpointMux,
} from './components';
import { CVIAgentView } from './components/cvi/CVIAgentView';
import { useAppDispatch, useAppSelector } from './store';
import { fetchScenarios, fetchLatestAllocation } from './store/slices/scenariosSlice';
import { hideSnackbar } from './store/slices/uiSlice';
import { useScenarioStream } from './hooks';

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { loading: scenariosLoading } = useAppSelector(
    (state) => state.scenarios
  );
  const { isRunning, isTransitioning, isResumeFlow, runId, result, pendingCheckpoint, whatifWeights, globalSupplyOverride, globalDemandOverride } = useAppSelector((state) => state.analysis);
  const { snackbar } = useAppSelector((state) => state.ui);
  const { runAnalysis } = useScenarioStream();

  const [activeTab, setActiveTab] = useState<'arbitrator' | 'cvi'>('arbitrator');

  // Track if we have an active stream to prevent re-triggering
  const streamActiveRef = useRef(false);
  const wasRunningRef = useRef(false);

  useEffect(() => {
    dispatch(fetchScenarios());
    dispatch(fetchLatestAllocation());
  }, [dispatch]);

  // Start SSE stream when a fresh analysis begins (transition from not running to running)
  useEffect(() => {
    const justStarted = isRunning && !wasRunningRef.current;
    wasRunningRef.current = isRunning;

    // Only start a new stream on a fresh start and when we don't already have one
    // CRITICAL: Skip if isResumeFlow (resumeAnalysis handles its own stream)
    // CRITICAL: Skip if any What-If override exists (ChatSidebar already started stream)
    const hasWhatIfOverride = whatifWeights || globalSupplyOverride || globalDemandOverride;
    if (runId && justStarted && !streamActiveRef.current && !isResumeFlow && !hasWhatIfOverride) {
      streamActiveRef.current = true;
      // Pass skipStateSetup=true since ActionBar already dispatched startAnalysis
      const cleanup = runAnalysis(runId, null, null, null, true);
      return () => {
        streamActiveRef.current = false;
        if (cleanup) cleanup();
      };
    }

    // Reset the stream ref when analysis fully completes (isRunning goes false, no checkpoint)
    if (!isRunning && !pendingCheckpoint) {
      streamActiveRef.current = false;
    }
  }, [runId, isRunning, runAnalysis, pendingCheckpoint, whatifWeights, globalSupplyOverride, globalDemandOverride]);

  const handleSnackbarClose = () => {
    dispatch(hideSnackbar());
  };

  if (scenariosLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }


  const hasResults = result && !isRunning && !pendingCheckpoint;

  const pageTitle = activeTab === 'arbitrator' ? 'Demand Reapportion Agent' : 'Customer Demand Preferences Agent';

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        minHeight: '100vh',
        paddingBottom: 3,
      }}
    >
      <Container maxWidth="xl" sx={{ padding: '16px' }}>
        <Header title={pageTitle} />

        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} aria-label="application mode tabs">
            <Tab label="Demand Reapportion Agent" value="arbitrator" />
            <Tab label="Customer Demand Preferences Agent" value="cvi" />
          </Tabs>
        </Box>

        {activeTab === 'cvi' ? (
          <CVIAgentView />
        ) : (
          <>
            <Grid container spacing={2} sx={{ marginBottom: '14px' }}>
              <Grid item xs={12} md={6}>
                <WhatSystemDoesCard />
              </Grid>
              <Grid item xs={12} md={6}>
                <LatestRecommendationCard />
              </Grid>
            </Grid>

            <ActionBar />


            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>

              <Grid item xs={12} lg={8} sx={{ display: 'flex' }}>
                {pendingCheckpoint ? (
                  <Box sx={{ width: '100%' }}>
                    <CheckpointMux />
                  </Box>
                ) : (hasResults || isRunning || isTransitioning) ? (
                  <Box sx={{ width: '100%' }}>
                    <ResultsPanel />
                  </Box>
                ) : (

                  <Card
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '14px 16px',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssessmentIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1a1a2e' }}>
                          Results
                        </Typography>
                      </Box>
                    </Box>
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        <Typography sx={{ fontSize: '14px', marginBottom: '8px' }}>
                          {isRunning ? 'Analysis in progress...' : 'No results yet'}
                        </Typography>
                        <Typography sx={{ fontSize: '12px' }}>
                          {isRunning
                            ? 'Please wait while agents process the deviation'
                            : 'Select a deviation and click Analyze to see results'}
                        </Typography>
                        {isRunning && (
                          <CircularProgress size={24} sx={{ marginTop: 2 }} />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Grid>


              <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '100%',
                    height: { xs: 'auto', lg: 'calc(100vh - 220px)' },
                    minHeight: 0,
                  }}
                >
                  <AgentOrchestrationNetwork />
                  <ActionFeed />
                </Box>
              </Grid>
            </Grid>
            <ChatSidebar />
          </>
        )}
      </Container>


      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default App;
